import React, { useEffect } from 'react';
import { X, CheckCircle, AlertTriangle, Info } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'info';

export interface ToastMessage {
  id: string;
  message: string;
  type: ToastType;
}

interface ToastProps {
  toasts: ToastMessage[];
  removeToast: (id: string) => void;
}

export const ToastContainer: React.FC<ToastProps> = ({ toasts, removeToast }) => {
  return (
    <div className="fixed bottom-24 md:bottom-8 right-4 z-[100] flex flex-col gap-2 pointer-events-none">
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} removeToast={removeToast} />
      ))}
    </div>
  );
};

const ToastItem: React.FC<{ toast: ToastMessage; removeToast: (id: string) => void }> = ({ toast, removeToast }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      removeToast(toast.id);
    }, 5000);
    return () => clearTimeout(timer);
  }, [toast.id, removeToast]);

  const icons = {
    success: <CheckCircle size={20} className="text-skjeta-green" />,
    error: <AlertTriangle size={20} className="text-red-500" />,
    info: <Info size={20} className="text-blue-400" />
  };

  const bgColors = {
    success: 'bg-zinc-900 border-skjeta-green/30',
    error: 'bg-zinc-900 border-red-500/30',
    info: 'bg-zinc-900 border-blue-500/30'
  };

  return (
    <div className={`${bgColors[toast.type]} border p-4 rounded-xl shadow-2xl flex items-center gap-3 min-w-[300px] animate-in slide-in-from-right-full pointer-events-auto`}>
      {icons[toast.type]}
      <p className="text-white text-sm font-bold flex-1">{toast.message}</p>
      <button onClick={() => removeToast(toast.id)} className="text-gray-500 hover:text-white">
        <X size={16} />
      </button>
    </div>
  );
};